<template>
  <div id="history">
    <div class="timeline" ref="timelineRef">
      <div class="entry" v-for="(item, index) in historyItems" :key="index">
        <div class="title">
          <h3>{{ item.year }}</h3>
        </div>
        <div class="incident" :class="'p' + (index + 1)">
          <h3>{{ t(item.titleKey) }}</h3>
          <p>{{ t(item.descriptionKey) }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import type { Ref } from 'vue';
import { useI18n } from 'vue-i18n';

interface HistoryItem {
  year: string;
  titleKey: string;
  descriptionKey: string;
}

const { t } = useI18n();

const historyItems = ref<HistoryItem[]>([
  { year: '2002', titleKey: 'his.02', descriptionKey: 'his.02-de' },
  { year: '2003', titleKey: 'his.03', descriptionKey: 'his.03-de' },
  { year: '2015', titleKey: 'his.15', descriptionKey: 'his.15-de' },
  { year: '2018', titleKey: 'his.18', descriptionKey: 'his.18-de' },
  { year: '2021', titleKey: 'his.21', descriptionKey: 'his.21-de' },
  { year: '2021', titleKey: 'his.21-2', descriptionKey: 'his.21-2-de' },
  { year: '2022', titleKey: 'his.22', descriptionKey: 'his.22-de' },
  { year: '2023', titleKey: 'his.23', descriptionKey: 'his.23-de' },
  { year: '2025', titleKey: 'his.25', descriptionKey: 'his.25-de' },
  { year: '2026', titleKey: 'his.26', descriptionKey: 'his.26-de' },
]);

const timelineRef: Ref<HTMLElement | null> = ref(null);

onMounted(async () => {
  // GSAPを動的インポートして初期バンドルサイズを削減
  const { gsap } = await import('gsap');

  // template refを使用してDOM要素に安全にアクセス（Vue 3ベストプラクティス）
  if (!timelineRef.value) {
    console.error('History: Timeline element not found');
    return;
  }

  const entries = timelineRef.value.querySelectorAll('.entry');

  // 各エントリーのアニメーション
  gsap.from(entries, {
    opacity: 0,
    y: 50,
    duration: 1,
    stagger: 0.3,
    ease: 'power2.out',
  });
});
</script>

<style scoped>
#history {
  width: 100%;
  max-width: 800px;
  overflow-y: hidden;
  margin: 0 auto;
}
.timeline {
  width: 100%;
  height: 100%;
  padding: 2rem 0 5rem 0;
  position: relative;
}
.timeline:before {
  content: '';
  position: absolute;
  left: calc(33% + 15px);
  top: calc(3rem + 3px);
  bottom: 0;
  width: 4px;
  background: #101010;
}
.timeline:after {
  content: '';
  display: table;
  clear: both;
}
.entry {
  clear: both;
  text-align: left;
  position: relative;
}
.title {
  float: left;
  width: 33%;
  padding-right: 30px;
  text-align: right;
  position: relative;
}
.title:before {
  /* point of timeline */
  content: '';
  position: absolute;
  width: 1rem;
  border: 0.5rem solid black;
  border-radius: 100%;
  top: 15%;
  right: calc(-1.5rem - 1px);
}
h3 {
  margin: 0;
  font-size: 120%;
}
p {
  margin: 0;
  font-size: small;
  color: #666;
  line-height: 1.6;
}
.incident {
  float: right;
  width: 66%;
  padding-left: 2rem;
  margin: 0;
}

/* 時代ごとの間隔 */
.p1 {
  /* 2002年 */
  margin-bottom: 0.5rem;
}
.p2 {
  /* 2003年 */
  margin-bottom: 6rem;
}
.p3 {
  /* 2015年 */
  margin-bottom: 1.5rem;
}
.p4 {
  /* 2018年 */
  margin-bottom: 1.5rem;
}
.p5 {
  /* 2021年 */
  margin-bottom: 0;
}
.p6 {
  /* 2021年 */
  margin-bottom: 0.5rem;
}
.p7 {
  /* 2022年 */
  margin-bottom: 0.5rem;
}
.p8 {
  /* 2023年 */
  margin-bottom: 1rem;
}
.p9 {
  /* 2026年 */
  margin-bottom: 1rem;
}

@media (max-width: 540px) {
  #history {
    transform: scale(0.75);
    transform-origin: top center;
  }
}
</style>
